import styles from './StatisticTimeCardListView.module.scss';

import { Box, Chip, Divider, Typography } from '@mui/material';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import { DateTime, Duration } from 'luxon';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getVacationDetails } from '@work4all/components/lib/dataDisplay/basic-table/utils/get-vacation-datiles';

import { WorkingTimeTimeCardDayInfo } from '@work4all/models/lib/Classes/WorkingTimeTimeCardDayInfo.entity';

import { DateTimeCustom } from '@work4all/utils/lib/date-utils/formatDateString';

type Props = {
  data: WorkingTimeTimeCardDayInfo[];
  timeAccountAtMonthEnd: number;
  onItemClick: (date: string) => void;
};

export const StatisticTimeCardListView = ({
  data,
  timeAccountAtMonthEnd,
  onItemClick,
}: Props) => {
  const { t } = useTranslation();

  const workingTimeAccountValue = useMemo(() => {
    return Duration.fromObject({ minutes: timeAccountAtMonthEnd })
      .toFormat('hh:mm')
      .replace(':-', ':');
  }, [timeAccountAtMonthEnd]);

  return (
    <div className={styles.container}>
      <div className={styles.workingTimeAccount}>
        <Typography color="var(--text03)">
          {t('COMMON.WORKING_TIME_ACCOUNT')}:
        </Typography>

        <Chip
          style={{
            color: 'white',
            backgroundColor:
              timeAccountAtMonthEnd < 0 ? 'var(--error)' : 'var(--success)',
            fontSize: '1rem',
          }}
          label={`${workingTimeAccountValue} h`}
        />
      </div>

      <Divider />

      <div className={styles.list}>
        {data.map((item, index) => (
          <Fragment key={index}>
            <ListItem
              data={item}
              onClick={() => {
                onItemClick(item.date);
              }}
            />
            <Divider />
          </Fragment>
        ))}
      </div>
    </div>
  );
};

type ListItemProps = {
  data: WorkingTimeTimeCardDayInfo;
  onClick: () => void;
};

const ListItem = ({ data, onClick }: ListItemProps) => {
  const { t } = useTranslation();

  const status = getStatus(data, t);
  const dateAsDateTime = DateTime.fromISO(data.date);
  const weekdayShort = dateAsDateTime.weekdayShort;
  const date = dateAsDateTime.toLocaleString(DateTimeCustom.DATE_SHORT);

  const formatedDate = useMemo(() => {
    return [
      ...(data.presenceList.length > 0
        ? data.presenceList
        : [{ start: null, end: null }]
      )
        .map((item) => [
          {
            label: t('COMMON.BEGINNING'),
            value: renderDateTime(item.start),
          },
          {
            label: t('COMMON.END'),
            value: renderDateTime(item.end),
          },
        ])
        .flat(),
      {
        label: t('COMMON.SHOULD'),
        value: renderDuration(data.targetMinutes),
      },
      {
        label: t('COMMON.PAUSE'),
        value: renderDuration(data.pauseAmountMinutes),
      },
      {
        label: t('COMMON.BALANCE'),
        value: renderDuration(data.timeAccountMinutes),
      },
      {
        label: t('COMMON.PAYED'),
        value: renderDuration(data.istNetMinutes),
      },
    ];
  }, [data, t]);

  const isWeekend = useMemo(() => {
    return Boolean(
      data.date && [6, 7].includes(DateTime.fromISO(data.date).weekday)
    );
  }, [data]);

  return (
    <Box
      className={clsx(styles.listItem, {
        [styles.publicHoliday]: Boolean(data.amountHoliday),
        [styles.vacation]: Boolean(data.amountVacation),
        [styles.sickness]: Boolean(data.amountSickness),
        [styles.weekend]: isWeekend,
      })}
      onClick={onClick}
    >
      <div className={styles.listItemHeader}>
        <div className={styles.listItemHeaderDate}>
          <Typography variant="body2" fontWeight="bold">
            {weekdayShort}.
          </Typography>

          <Typography variant="body2">{date}</Typography>
        </div>

        {status && (
          <Typography
            variant="body1"
            className={clsx(styles.listItemHeaderStatus, {
              [styles.publicHolidayStatus]: Boolean(data.amountHoliday),
              [styles.vacationStatus]: Boolean(data.amountVacation),
              [styles.sicknessStatus]: Boolean(data.amountSickness),
            })}
            bgcolor={
              data?.vacationList?.[0]?.vacationKind?.hexColorPair?.split(',')[0]
            }
          >
            {status}
          </Typography>
        )}
      </div>

      <div className={styles.listItemContent}>
        {formatedDate.map((item, index) => (
          <div key={index} className={styles.listItemContentItem}>
            <Typography variant="body2" className={styles.label}>
              {item.label}:
            </Typography>
            <Typography variant="body2" className={styles.value}>
              {item.value}
            </Typography>
          </div>
        ))}
      </div>
    </Box>
  );
};

const NULLABLE_DATE_VALUE = '...';

const renderDateTime = (date: string) => {
  if (!date || date === '0001-01-01T00:00:00') {
    return NULLABLE_DATE_VALUE;
  }

  const dateAsDateTime = DateTime.fromISO(date);

  if (!dateAsDateTime.isValid) {
    return NULLABLE_DATE_VALUE;
  }

  return dateAsDateTime.toLocaleString(DateTime.TIME_SIMPLE);
};

const renderDuration = (minutes: number) => {
  return minutes ? Duration.fromObject({ minutes }).toFormat('hh:mm') : '...';
};

const getStatus = (data: WorkingTimeTimeCardDayInfo, t: TFunction) => {
  if (data.amountSickness > 0) {
    return t('COMMON.SICKNESSDAY');
  }

  if (data.amountHoliday > 0) {
    return t('COMMON.HOLIDAY');
  }

  const vacationDetails = getVacationDetails(data.vacationList);

  if (vacationDetails.isHalfDay) {
    return t('COMMON.VACATION_HALF_DAY');
  }

  if (vacationDetails.isFullDay) {
    return t('COMMON.VACATIONDAY');
  }

  return null;
};
